import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import createCustomer from '@/shared/api/customers/createCustomer';
import CreateCustomerModal from '@/shared/components/dialogs/CreateCustomerModal';
import WaveUIProgressDialog from '@/shared/components/dialogs/WaveUIProgressDialog';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Customer from '@/shared/types/customer.type';
import DialogName from '@/shared/types/dialogName.enum';

import CustomerTable_v2 from './components/CustomerTable_v2/CustomerTable_v2';
import useCustomerTable_v2 from './components/CustomerTable_v2/hooks/useCustomerTable_v2';
import Title from './components/Title';

const Customers: FC = () => {
  const { t } = useTranslation(['customers', 'common']);
  const redirectLogin = useRedirectLogin();

  const { dialog, onUpdateDialogState } = useModalDialog<unknown, DialogName>();
  const { progressDialog, onUpdateProgressDialog } = useProgressDialog();
  const { notificationBar, onUpdateNotificationBar } = useNotificationBar();

  const customerTable_v2State = useCustomerTable_v2();

  const handleCreateCustomer = async (customer: Partial<Customer>) => {
    onUpdateDialogState(DialogName.CreateCustomer, { loading: true });
    onUpdateProgressDialog({
      title: t('customers:creatingCustomer'),
      open: true,
      progress: 0,
    });

    const response = await createCustomer(customer, redirectLogin);

    if (response.success) {
      onUpdateDialogState(DialogName.CreateCustomer, {
        open: false,
        loading: false,
      });
      onUpdateProgressDialog({ open: false });
      onUpdateNotificationBar({
        message: t('customers:customerCreated'),
        severity: 'success',
        open: true,
      });
    } else {
      onUpdateDialogState(DialogName.CreateCustomer, {
        loading: false,
        errors: [response.msg],
      });
      onUpdateProgressDialog({ open: false });
    }
  };

  return (
    <>
      <Title />

      <CustomerTable_v2
        customerTable_v2State={customerTable_v2State}
        onCreateCustomer={() => {
          onUpdateDialogState(DialogName.CreateCustomer, {
            open: true,
            errors: [],
          });
        }}
      />

      {dialog?.createCustomer?.open && (
        <CreateCustomerModal
          open={dialog?.createCustomer?.open}
          onClose={() => {
            onUpdateDialogState(DialogName.CreateCustomer, { open: false });
          }}
          proceed={(customer) => {
            void handleCreateCustomer(customer);
          }}
        />
      )}

      <WaveUINotificationBar
        message={notificationBar.message}
        severity={notificationBar.severity}
        open={notificationBar.open}
        close={() => onUpdateNotificationBar({ open: false })}
      />

      <WaveUIProgressDialog
        title={progressDialog?.title}
        progress={progressDialog?.progress || 0}
        open={progressDialog?.open || false}
        onClose={() => onUpdateProgressDialog({ open: false })}
      />
    </>
  );
};

export default Customers;
